import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

export const useThemeStore = defineStore('theme', () => {
  // Default to light theme
  const currentTheme = ref('light')

  // Initialize theme from localStorage or system preference
  const initializeTheme = () => {
    const savedTheme = localStorage.getItem('theme')

    if (savedTheme) {
      currentTheme.value = savedTheme
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      currentTheme.value = prefersDark ? 'dark' : 'light'
    }

    applyTheme(currentTheme.value)
  }

  // Apply theme to document
  const applyTheme = (theme) => {
    const root = document.documentElement

    if (theme === 'dark') {
      root.setAttribute('data-theme', 'dark')
    } else {
      root.removeAttribute('data-theme')
    }
  }

  // Toggle between themes
  const toggleTheme = () => {
    currentTheme.value = currentTheme.value === 'light' ? 'dark' : 'light'
  }

  // Set specific theme
  const setTheme = (theme) => {
    if (theme === 'light' || theme === 'dark') {
      currentTheme.value = theme
    }
  }

  // Computed properties
  const isDark = () => currentTheme.value === 'dark'
  const isLight = () => currentTheme.value === 'light'

  // Get theme display name
  const getThemeDisplayName = () => {
    return currentTheme.value === 'dark' ? 'Tema Oscuro' : 'Tema Claro'
  }

  // Get opposite theme name
  const getOppositeThemeName = () => {
    return currentTheme.value === 'dark' ? 'Cambiar a Claro' : 'Cambiar a Oscuro'
  }

  // Watch for theme changes and persist to localStorage
  watch(currentTheme, (newTheme) => {
    localStorage.setItem('theme', newTheme)
    applyTheme(newTheme)
  })

  // Listen for system theme changes
  const setupSystemThemeListener = () => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    mediaQuery.addEventListener('change', (e) => {
      // Only update if no saved preference exists
      const savedTheme = localStorage.getItem('theme')
      if (!savedTheme) {
        currentTheme.value = e.matches ? 'dark' : 'light'
      }
    })
  }

  return {
    currentTheme,
    initializeTheme,
    toggleTheme,
    setTheme,
    isDark,
    isLight,
    getThemeDisplayName,
    getOppositeThemeName,
    setupSystemThemeListener
  }
})